import { Component, inject, output, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { PaymentService } from '../../../../core/services/payment/payment.service';
import { PaymentHistoryModel } from '../../../../core/models/data/payment-history.model';
import { PaymentIntentModel } from '../../../../core/models/data/payment.model';
import { BalancesMessagingConstants } from '../../../../core/constants/messaging.constants';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';

@Component({
	selector: 'ms-cancel-refund-modal',
	imports: [
		CommonModule,
		FontAwesomeModule,
		InfoModalComponent,
		EscCloseDirective
	],
	templateUrl: './cancel-refund-modal.component.html'
})
export class CancelRefundModalComponent extends BaseFormComponent {

	//#region Properties & Signals

	public cancelCompleted = output<PaymentIntentModel>();

	public payment = signal<PaymentHistoryModel | undefined>(undefined);

	public confirmationMessage = BalancesMessagingConstants.CANCEL_REFUND_CONFIRMATION();

	@ViewChild('cancelRefundInfoRef') infoRef!: InfoModalComponent;

	//#endregion Properties & Signals

	//#region Constructor

	private paymentService = inject(PaymentService);
	private bootstrapService = inject(BootstrapService);

	constructor() {
		super();
	}

	//#endregion Constructor

	//#region Public Methods

	public open(payment: PaymentHistoryModel): void {
		this.payment.set(payment);
		this.bootstrapService.manageModal('#cancelRefundModal', ModalActions.Show);
	}

	//#endregion Public Methods

	//#region Form Submission

	public async onSubmit(): Promise<void> {
		const payment = this.payment();
		const paymentId = payment?.paymentId;
		if (paymentId === undefined) return;

		this.isSubmitting = true;

		try {
			const result = await this.paymentService.cancelRefund(paymentId);

			this.bootstrapService.manageModal('#cancelRefundModal', ModalActions.Hide);

			if (result.success && result.data) {
				this.infoRef.modalType.set(ModalTypes.Success);
				this.infoRef.message.set(BalancesMessagingConstants.CANCEL_REFUND_SUCCESS());
				this.bootstrapService.manageModal('#cancelRefundInfoModal', ModalActions.Show);
				this.cancelCompleted.emit(result.data);
			} else {
				this.infoRef.modalType.set(ModalTypes.Failure);
				this.infoRef.message.set(result.message || BalancesMessagingConstants.CANCEL_REFUND_FAILURE());
				this.bootstrapService.manageModal('#cancelRefundInfoModal', ModalActions.Show);
			}
		} catch {
			this.bootstrapService.manageModal('#cancelRefundModal', ModalActions.Hide);
			this.infoRef.modalType.set(ModalTypes.Failure);
			this.infoRef.message.set(BalancesMessagingConstants.CANCEL_REFUND_FAILURE());
			this.bootstrapService.manageModal('#cancelRefundInfoModal', ModalActions.Show);
		} finally {
			this.isSubmitting = false;
		}
	}

	//#endregion Form Submission

	//#region Modal Management

	public onCancel(): void {
		this.bootstrapService.manageModal('#cancelRefundModal', ModalActions.Hide);
	}

	public infoModalConfirmed(): void {
		this.bootstrapService.manageModal('#cancelRefundInfoModal', ModalActions.Hide);
	}

	//#endregion Modal Management
}
